import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import { formatPhone } from '../lib/phone';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Toast, ui, fmtDate, asList,
} from '../components/adminUi';
import Icon from '../components/Icon';

// Backend (routes/admin/adminPanel.js): GET /api/admin/user-reports, PATCH /api/admin/user-reports/:id.
// Holatlar: pending | reviewed | resolved | dismissed.
const STATUS_LABEL = {
  pending: 'Kutilmoqda', reviewed: "Ko'rib chiqildi", resolved: 'Hal qilindi', dismissed: 'Rad etildi',
};
const STATUS_TONE = { pending: 'amber', reviewed: 'blue', resolved: 'green', dismissed: 'red' };
const statusLabel = (st) => STATUS_LABEL[st] || st || '—';

const STATUS_FILTERS = [
  ['', 'Barchasi'],
  ['pending', STATUS_LABEL.pending],
  ['reviewed', STATUS_LABEL.reviewed],
  ['resolved', STATUS_LABEL.resolved],
  ['dismissed', STATUS_LABEL.dismissed],
];

// Mijoz ilovasidagi sabab ro'yxati bilan bir xil (mobile: src/moderation/moderationApi.ts REPORT_REASONS).
const REASON_LABEL = {
  inappropriate: 'Nomaqbul kontent', spam: 'Spam', scam: 'Aldov', other: 'Boshqa',
};
const reasonLabel = (r) => REASON_LABEL[String(r || '').toLowerCase()] || r || '—';

const ROLE_LABEL = { client: 'Mijoz', provider: 'Usta', admin: 'Admin' };
const roleLabel = (r) => ROLE_LABEL[r] || r || '';

const PAGE_LIMIT = 20;

function Party({ name, phone, role }) {
  if (!name && !phone) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
  return (
    <div>
      <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>{name || '—'}</div>
      {phone && <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{formatPhone(phone)}</div>}
      {role && <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{roleLabel(role)}</div>}
    </div>
  );
}

export default function UserReportsPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [actingId, setActingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const q = new URLSearchParams({ page: String(page), limit: String(PAGE_LIMIT) });
      if (statusFilter) q.set('status', statusFilter);
      const res = await apiFetch(`/api/admin/user-reports?${q.toString()}`, auth.token);
      if (!res.ok) throw new Error();
      const body = await res.json().catch(() => ({}));
      const payload = body && body.data && !Array.isArray(body.data) ? body.data : body;
      setItems(asList(payload));
      setTotal(Number(payload && payload.total) || 0);
    } catch {
      setItems([]);
      setTotal(0);
      setFetchError('Shikoyatlarni yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const applyStatus = (value) => { setStatusFilter(value); setPage(1); };

  const changeStatus = async (row, status) => {
    setActingId(row.id);
    try {
      const res = await apiFetch(`/api/admin/user-reports/${row.id}`, auth.token, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setToast({ type: 'error', text: body.message || "Holatni o'zgartirib bo'lmadi." });
        return;
      }
      const updated = body.data || { ...row, status };
      setItems((list) => list.map((it) => (it.id === row.id ? { ...it, ...updated } : it)));
      setToast({ type: 'success', text: `Shikoyat #${row.id}: ${statusLabel(status)}` });
    } catch {
      setToast({ type: 'error', text: "Tarmoq xatosi. Qayta urinib ko'ring." });
    } finally {
      setActingId(null);
    }
  };

  const openTarget = (row) => {
    const key = row.targetPhone ? String(row.targetPhone).replace(/\D/g, '') : row.targetName;
    navigate(`/admin/users${key ? `?search=${encodeURIComponent(key)}` : ''}`);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <AdminLayout>
      <PageHeader title="Shikoyatlar" subtitle="Mijoz va ustalar yuborgan shikoyatlarni ko'rib chiqish">
        <button style={ui.pagerBtn} onClick={load}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      <div style={s.chips} role="group" aria-label="Holat filtri">
        {STATUS_FILTERS.map(([value, label]) => {
          const active = statusFilter === value;
          return (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => applyStatus(value)}
              aria-pressed={active}
              style={{ ...s.chip, ...(active ? s.chipActive : {}) }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        fetchError ? null : (
          <Empty
            icon="flag"
            title="Shikoyatlar hali yo'q"
            hint={statusFilter ? "Filtrni o'zgartirib ko'ring." : "Yuborilgan shikoyatlar bu yerda ko'rinadi."}
          />
        )
      ) : (
        <>
          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: '48px' }}>ID</th>
                  <th style={ui.th}>Kim yubordi</th>
                  <th style={ui.th}>Kimga</th>
                  <th style={ui.th}>Sabab</th>
                  <th style={ui.th}>Tavsif</th>
                  <th style={ui.th}>Holat</th>
                  <th style={ui.th}>Sana</th>
                  <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const busy = actingId === r.id;
                  return (
                    <tr key={r.id} style={ui.tr}>
                      <td style={{ ...ui.td, color: 'var(--color-text-muted)' }}>{r.id}</td>
                      <td style={ui.td}><Party name={r.reporterName} phone={r.reporterPhone} role={r.reporterRole} /></td>
                      <td style={ui.td}><Party name={r.targetName} phone={r.targetPhone} role={r.targetRole} /></td>
                      <td style={ui.td}>{reasonLabel(r.reason)}</td>
                      <td style={{ ...ui.td, maxWidth: '260px', wordBreak: 'break-word' }}>{r.description || '—'}</td>
                      <td style={ui.td}><Badge tone={STATUS_TONE[r.status] || 'gray'}>{statusLabel(r.status)}</Badge></td>
                      <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt, true)}</td>
                      <td style={{ ...ui.td, textAlign: 'right' }}>
                        <div style={ui.actions}>
                          {r.status !== 'reviewed' && r.status !== 'resolved' && (
                            <button
                              style={{ ...ui.btnSm, ...ui.btnGreen, ...(busy ? ui.btnDisabled : {}) }}
                              disabled={busy}
                              onClick={() => changeStatus(r, 'reviewed')}
                            >
                              Ko'rib chiqildi
                            </button>
                          )}
                          {r.status !== 'dismissed' && (
                            <button
                              style={{ ...ui.btnSm, ...ui.btnRed, ...(busy ? ui.btnDisabled : {}) }}
                              disabled={busy}
                              onClick={() => changeStatus(r, 'dismissed')}
                            >
                              Rad etish
                            </button>
                          )}
                          {(r.targetUserId || r.targetName) && (
                            <button style={{ ...ui.btnSm, ...ui.btnBlue }} onClick={() => openTarget(r)}>
                              Foydalanuvchi
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={ui.pager}>
            <button
              style={{ ...ui.pagerBtn, ...(page <= 1 ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ← Oldingi
            </button>
            <span style={ui.pagerInfo}>{page} / {totalPages} · jami {total} ta</span>
            <button
              style={{ ...ui.pagerBtn, ...(page >= totalPages ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Keyingi →
            </button>
          </div>
        </>
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </AdminLayout>
  );
}

const s = {
  chips: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  chip: {
    height: '36px',
    padding: '0 14px',
    whiteSpace: 'nowrap',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-chip)',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  chipActive: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    borderColor: 'var(--color-primary)',
    fontWeight: '700',
  },
};
